import { OrderNoteModel } from '@/http/models/OrderNote'
import { toast } from 'sonner'

import { useState } from 'react'

import { OrderNoteModel as OrderNoteType } from '@/types'

import { useFetchOrderNotes } from './useFetchOrderNotes'

type DialogMode = 'create' | 'edit' | 'view'

export const useOrderNotesTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [orderNoteDialogOpen, setOrderNoteDialogOpen] = useState(false)
  const [editOrderNote, setEditOrderNote] = useState<OrderNoteType.OrderNote | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [orderNoteToDelete, setOrderNoteToDelete] = useState<OrderNoteType.OrderNote | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchOrderNotes({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditOrderNote(null)
    setOrderNoteDialogOpen(true)
  }

  const openViewDialog = (orderNote: OrderNoteType.OrderNote) => {
    setMode('view')
    setEditOrderNote(orderNote)
    setOrderNoteDialogOpen(true)
  }

  const openEditDialog = (orderNote: OrderNoteType.OrderNote) => {
    setMode('edit')
    setEditOrderNote(orderNote)
    setOrderNoteDialogOpen(true)
  }

  const closeDialog = () => {
    setOrderNoteDialogOpen(false)
    setEditOrderNote(null)
  }

  const handleDeleteClick = (orderNote: OrderNoteType.OrderNote) => {
    setOrderNoteToDelete(orderNote)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!orderNoteToDelete?._id) return

    setDeleteLoading(true)
    const response = await OrderNoteModel.delete({
      params: { id: orderNoteToDelete._id },
    })

    if (response.success) {
      toast.success('Order note deleted successfully')
      setDeleteDialogOpen(false)
      setOrderNoteToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete order note')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (orderNote: OrderNoteType.OrderNote) => {
    const newStatus = orderNote.status === 'active' ? 'inactive' : 'active'

    const response = await OrderNoteModel.update({
      params: { id: orderNote._id },
      body: { status: newStatus, content: orderNote.content },
    })

    if (response.success) {
      toast.success('Order note status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateOrderNote = async (data: any) => {
    setCreateLoading(true)
    const response = await OrderNoteModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Order note created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create order note')
    }
    setCreateLoading(false)
  }

  const handleUpdateOrderNote = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await OrderNoteModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Order note updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update order note')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalOrderNotes: pagination.totalCount,
    activeOrderNotes: data.filter((note) => note.status === 'active').length,
    inactiveOrderNotes: data.filter((note) => note.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,
    mode,
    orderNoteDialogOpen,
    editOrderNote,
    deleteDialogOpen,
    orderNoteToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateOrderNote,
    handleUpdateOrderNote,
    toggleStatus,
  }
}
